/**
 * Document Service
 * Handles all document-related API calls including upload to MinIO
 */

import { API_CONFIG, ENDPOINTS } from '../config/api';

export interface DocumentUploadData {
  patient_id: string;
  title: string;
  document_type?: string;
  description?: string;
  provider_id?: string;  // The provider the document is FROM/associated with
  file?: File;
  content?: string;
}

export interface DocumentResponse {
  id: string;
  title: string;
  file_name: string;
  file_path: string;
  user_id: string;  // User who uploaded the document
  upload_provider_id: string;  // Provider the document is FROM/associated with
  document_date: string;  // Date of the document
  created_at: string;  // Timestamp when uploaded
  updated_at: string;  // Last modification timestamp
}

export interface Document {
  id: string;
  patient_id: string;
  user_id: string;  // User who uploaded the document
  upload_provider_id: string;  // Provider the document is FROM/associated with
  title: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  document_type: string;
  document_date: string;  // Date of the document itself
  description: string;
  is_archived: boolean;
  created_at: string;  // When the document was uploaded (for sorting newest to oldest)
  updated_at: string;
}

class DocumentService {
  private getAuthHeaders(token: string): HeadersInit {
    return {
      'Authorization': `Token ${token}`,
    };
  }

  /**
   * Upload a new document to MinIO
   * Supports both file uploads and text content
   */
  async uploadDocument(data: DocumentUploadData, token: string): Promise<DocumentResponse> {
    const formData = new FormData();
    
    formData.append('patient_id', data.patient_id);
    formData.append('title', data.title);
    
    if (data.document_type) {
      formData.append('document_type', data.document_type);
    }
    
    if (data.description) {
      formData.append('description', data.description);
    }
    
    if (data.provider_id) {
      formData.append('provider_id', data.provider_id);
    }
    
    if (data.file) {
      formData.append('file', data.file);
    } else if (data.content) {
      formData.append('content', data.content);
    }

    const response = await fetch(`${API_CONFIG.FULL_BASE}${ENDPOINTS.DOCUMENTS.CREATE}`, {
      method: 'POST',
      headers: this.getAuthHeaders(token),
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload document');
    }

    return response.json();
  }

  /**
   * Upload a document blob (e.g., from Syncfusion DocumentEditor)
   * Converts the blob to a file and uploads to MinIO
   */
  async uploadDocumentBlob(
    blob: Blob,
    patientId: string,
    title: string,
    documentType: string = 'clinical_note',
    description: string = '',
    providerId: string = '',
    token: string
  ): Promise<DocumentResponse> {
    // Convert blob to file with appropriate extension
    const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`;
    const file = new File([blob], fileName, { type: blob.type });

    return this.uploadDocument(
      {
        patient_id: patientId,
        title,
        document_type: documentType,
        description,
        provider_id: providerId,
        file,
      },
      token
    );
  }

  /**
   * Get list of documents
   */
  async getDocuments(token: string, params?: Record<string, string>): Promise<Document[]> {
    const queryString = params 
      ? '?' + new URLSearchParams(params).toString()
      : '';

    const response = await fetch(
      `${API_CONFIG.FULL_BASE}${ENDPOINTS.DOCUMENTS.LIST}${queryString}`,
      {
        headers: this.getAuthHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch documents');
    }

    return response.json();
  }

  /**
   * Get a single document by ID
   */
  async getDocument(id: string, token: string): Promise<Document> {
    const response = await fetch(
      `${API_CONFIG.FULL_BASE}${ENDPOINTS.DOCUMENTS.DETAIL}${id}/`,
      {
        headers: this.getAuthHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch document');
    }

    return response.json();
  }

  /**
   * Download document file from MinIO
   */
  async downloadDocument(id: string, token: string): Promise<Blob> {
    const response = await fetch(
      `${API_CONFIG.FULL_BASE}${ENDPOINTS.DOCUMENTS.DOWNLOAD}${id}/download/`,
      {
        headers: this.getAuthHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to download document');
    }

    return response.blob();
  }

  /**
   * Get document content (for editing)
   */
  async getDocumentContent(id: string, token: string): Promise<any> {
    const response = await fetch(
      `${API_CONFIG.FULL_BASE}${ENDPOINTS.DOCUMENTS.CONTENT}${id}/content/`,
      {
        headers: this.getAuthHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch document content');
    }

    return response.json();
  }

  /**
   * Update an existing document
   * Updates both metadata and optionally the file content
   */
  async updateDocument(
    id: string,
    data: Partial<DocumentUploadData>,
    token: string
  ): Promise<DocumentResponse> {
    const formData = new FormData();
    
    if (data.patient_id) {
      formData.append('patient_id', data.patient_id);
    }
    
    if (data.title) {
      formData.append('title', data.title);
    }
    
    if (data.document_type) {
      formData.append('document_type', data.document_type);
    }
    
    if (data.description !== undefined) {
      formData.append('description', data.description);
    }
    
    if (data.provider_id) {
      formData.append('provider_id', data.provider_id);
    }
    
    if (data.file) {
      formData.append('file', data.file);
    } else if (data.content) {
      formData.append('content', data.content);
    }

    const response = await fetch(
      `${API_CONFIG.FULL_BASE}${ENDPOINTS.DOCUMENTS.DETAIL}${id}/`,
      {
        method: 'PATCH',
        headers: this.getAuthHeaders(token),
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update document');
    }

    return response.json();
  }

  /**
   * Update a document with a blob (e.g., from Syncfusion DocumentEditor)
   * Converts the blob to a file and updates the document in MinIO
   */
  async updateDocumentBlob(
    id: string,
    blob: Blob,
    patientId: string,
    title: string,
    documentType: string = 'clinical_note',
    description: string = '',
    providerId: string = '',
    token: string
  ): Promise<DocumentResponse> {
    // Convert blob to file with appropriate extension
    const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`;
    const file = new File([blob], fileName, { type: blob.type });

    return this.updateDocument(
      id,
      {
        patient_id: patientId,
        title,
        document_type: documentType,
        description,
        provider_id: providerId,
        file,
      },
      token
    );
  }

  /**
   * Delete a document
   */
  async deleteDocument(id: string, token: string): Promise<void> {
    const response = await fetch(
      `${API_CONFIG.FULL_BASE}${ENDPOINTS.DOCUMENTS.DETAIL}${id}/`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders(token),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete document');
    }
  }
}

export const documentService = new DocumentService();
